import { Component, OnInit } from '@angular/core';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { FormFieldModule, TextBoxModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AuthenticationService } from '../../services/authentication/authentication.service';
import { catchError, take, tap } from 'rxjs';
import { Router } from '@angular/router';
import { AfRoutes } from '../../routes';
import { FormErrorComponent } from '../../shared/components/form-error/form-error.component';
import { oneUppercaseCharacterValidator } from '../../shared/directives/form-validators/one-uppercase-character.directive';
import { oneLowercaseCharacterValidator } from '../../shared/directives/form-validators/one-lowercase-character.directive';
import { oneDigitCharacterValidator } from '../../shared/directives/form-validators/one-digit-character.directive';
import { oneSpecialCharacterValidator } from '../../shared/directives/form-validators/one-special-character.directive';
import { notAllowedCharactersValidator } from '../../shared/directives/form-validators/not-allowed-characters.directive';
import { compareFieldsValidator } from '../../shared/directives/form-validators/compare-fields.directive';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'af-confirm-reset-password',
  imports: [
    ButtonModule,
    CommonModule,
    FormFieldModule,
    LabelModule,
    ReactiveFormsModule,
    TextBoxModule,
    FormErrorComponent,
  ],
  templateUrl: './confirm-reset-password.component.html',
  styleUrl: './confirm-reset-password.component.scss',
})
export class ConfirmResetPasswordComponent implements OnInit {
  confirmResetPasswordForm: FormGroup = new FormGroup(
    {
      username: new FormControl(''),
      newPassword: new FormControl('', [
        Validators.required,
        Validators.minLength(12),
        oneUppercaseCharacterValidator(),
        oneLowercaseCharacterValidator(),
        oneDigitCharacterValidator(),
        oneSpecialCharacterValidator(),
        notAllowedCharactersValidator(),
      ]),
      confirmPassword: new FormControl(''),
      confirmationCode: new FormControl(''),
    },
    [compareFieldsValidator('newPassword', 'confirmPassword')]
  );

  errorMessage?: string;

  validatorErrors: { [key: string]: boolean } = {};

  constructor(
    private authenticationService: AuthenticationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.updateValidatorErrors();
    this.confirmResetPasswordForm.valueChanges
      .pipe(
        tap(() => {
          this.clearErrorMessage();
          this.updateValidatorErrors();
        })
      )
      .subscribe();
  }

  confirmResetPassword(): void {
    if (this.confirmResetPasswordForm.valid) {
      const username = this.confirmResetPasswordForm.get('username')?.value;
      const newPassword =
        this.confirmResetPasswordForm.get('newPassword')?.value;
      const confirmationCode =
        this.confirmResetPasswordForm.get('confirmationCode')?.value;
      this.authenticationService
        .confirmResetPassword({ username, newPassword, confirmationCode })
        .pipe(
          tap(() => this.router.navigate([AfRoutes.login])),
          take(1),
          catchError((error) => {
            this.errorMessage = error.message;
            throw error;
          })
        )
        .subscribe();
    }
  }

  private clearErrorMessage(): void {
    this.errorMessage = undefined;
  }

  private updateValidatorErrors(): void {
    const newPassword = this.confirmResetPasswordForm.get('newPassword');
    this.validatorErrors['required'] = Boolean(
      newPassword?.errors && newPassword.errors['required']
    );
    this.validatorErrors['minlength'] =
      Boolean(newPassword?.errors && newPassword.errors['required']) ||
      Boolean(
        newPassword?.errors &&
          newPassword.errors['minlength'] &&
          newPassword.errors['minlength']['requiredLength'] !==
            newPassword.errors['minlength']['actualLength']
      );
    this.validatorErrors['oneUppercaseCharacter'] = Boolean(
      newPassword?.errors && newPassword.errors['oneUppercaseCharacter']
    );
    this.validatorErrors['oneLowercaseCharacter'] = Boolean(
      newPassword?.errors && newPassword.errors['oneLowercaseCharacter']
    );
    this.validatorErrors['oneDigitCharacter'] = Boolean(
      newPassword?.errors && newPassword.errors['oneDigitCharacter']
    );
    this.validatorErrors['oneSpecialCharacter'] = Boolean(
      newPassword?.errors && newPassword.errors['oneSpecialCharacter']
    );
    this.validatorErrors['notAllowedCharacters'] = Boolean(
      newPassword?.errors && newPassword.errors['notAllowedCharacters']
    );
    this.validatorErrors['compareFields'] =
      Boolean(newPassword?.errors && newPassword.errors['required']) ||
      Boolean(
        this.confirmResetPasswordForm?.errors &&
          this.confirmResetPasswordForm.errors['compareFields']
      );
  }
}
